import { useMemo } from 'react';
import { Button, Flex, Tag, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OemPartnerGroupDto } from '@evimo/client/models';
import { SlaPartnerKindDot } from '~/app/landing/sla/shared/components/SlaPartnerKindDot';
import { SlaStatusTag } from '~/app/landing/sla/shared/components/SlaStatusTag';
import { formatDateLabel } from '~/app/landing/sla/shared/utils/sla-format.utils';
import type { MaintenanceSlaContractRecord } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';

type Args = {
  contracts: Record<string, MaintenanceSlaContractRecord>;
  canManage: boolean;
  onEdit: (row: OemPartnerGroupDto) => void;
};

export function useMaintenanceSlaRulesColumns({
  contracts,
  canManage,
  onEdit,
}: Args) {
  const { t } = useTranslation('sla');

  return useMemo(
    () => [
      {
        title: t('oems.columns.partner'),
        dataIndex: 'partnerName',
        key: 'partnerName',
        render: (name: string) => (
          <Flex align="center" gap={10}>
            <SlaPartnerKindDot variant="oem" />
            <Typography.Text strong>{name}</Typography.Text>
          </Flex>
        ),
      },
      {
        title: t('oems.maintenance.fields.validFrom'),
        key: 'validFrom',
        render: (_: unknown, row: OemPartnerGroupDto) =>
          row.slaContract
            ? formatDateLabel(row.slaContract.validFrom)
            : t('oems.maintenance.dash'),
      },
      {
        title: t('oems.maintenance.fields.validTo'),
        key: 'validTo',
        render: (_: unknown, row: OemPartnerGroupDto) => {
          if (!row.slaContract) {
            return t('oems.maintenance.dash');
          }
          return row.slaContract.validTo
            ? formatDateLabel(row.slaContract.validTo)
            : t('review.openEnded');
        },
      },
      {
        title: t('oems.columns.status'),
        key: 'status',
        width: 140,
        render: (_: unknown, row: OemPartnerGroupDto) =>
          row.slaContract ? (
            <SlaStatusTag status={row.slaContract.status} />
          ) : (
            t('oems.maintenance.dash')
          ),
      },
      {
        title: t('oems.maintenance.rulesColumn'),
        key: 'rules',
        width: 160,
        render: (_: unknown, row: OemPartnerGroupDto) => {
          const rules = contracts[row.relationshipId];
          if (!rules) {
            return t('oems.maintenance.rulesMissing');
          }
          if (rules.rulesStatus === 'DRAFT') {
            return (
              <Flex align="center" gap={6}>
                <Tag color="gold">{t('oems.maintenance.rulesDraftBadge')}</Tag>
              </Flex>
            );
          }
          return t('oems.maintenance.rulesReady');
        },
      },
      ...(canManage
        ? [
            {
              title: '',
              key: 'actions',
              width: 220,
              fixed: 'right' as const,
              render: (_: unknown, row: OemPartnerGroupDto) => {
                const rules = contracts[row.relationshipId];
                const isDraft = rules?.rulesStatus === 'DRAFT';
                return (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    disabled={!row.slaContract}
                    onClick={() => onEdit(row)}
                  >
                    {isDraft
                      ? t('oems.maintenance.continueEditing')
                      : rules
                        ? t('actions.edit')
                        : t('oems.maintenance.add')}
                  </Button>
                );
              },
            },
          ]
        : []),
    ],
    [canManage, contracts, onEdit, t],
  );
}
